// src/pages/Cursos.tsx
// UPDATED: 2026-03-27 — Redesenhado no padrão visual do site (âmbar/dourado)

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  BookOpen, Search, Star, Users, Clock,
  Loader2, AlertTriangle, Building2, Filter,
  ArrowRight, GraduationCap,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { API_BASE } from '../utils/apiConfig';

// ─── Types ───────────────────────────────────────────────────────────────────
interface Curso {
  id: string;
  titulo: string;
  descricao: string;
  preco: number;
  categoria: string;
  nivel: 'basico' | 'intermediario' | 'avancado';
  modalidade: 'presencial' | 'online' | 'hibrido';
  instrutor: string;
  afiliado: string;
  alunos: number;
  duracao: string;
  avaliacao: number;
  coverImageUrl: string | null;
  externalUrl: string | null;
}

const mapApiCurso = (c: any): Curso => ({
  id:            c.id,
  titulo:        c.courseName ?? c.nome ?? '',
  descricao:     c.description ?? c.descricao ?? '',
  preco:         Number(c.price ?? c.preco ?? 0),
  categoria:     c.category ?? c.categoria ?? '',
  nivel:         (c.level ?? c.nivel ?? 'basico') as Curso['nivel'],
  modalidade:    (c.modality ?? c.modalidade ?? 'presencial') as Curso['modalidade'],
  instrutor:     c.instructorName ?? c.instrutor ?? '',
  afiliado:      c.affiliate?.schoolName ?? c.affiliateName ?? c.afiliado ?? '',
  alunos:        c.studentsCount ?? c.alunos ?? 0,
  duracao:       c.durationHours ? `${c.durationHours}h` : '—',
  avaliacao:     Number(c.rating ?? c.avaliacao ?? 0),
  coverImageUrl: c.coverImageUrl ?? c.cover_image_url ?? c.imageUrl ?? c.image ?? c.thumbnail ?? null,
  externalUrl:   c.externalUrl ?? null,
});

const ITEMS_PER_PAGE = 9;

const Cursos: React.FC = () => {
  const [cursos, setCursos]     = useState<Curso[]>([]);
  const [totalApi, setTotalApi] = useState(0);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState<string | null>(null);

  const [buscaInput, setBuscaInput]       = useState('');
  const [busca, setBusca]                 = useState('');
  const [categoriaFiltro, setCategoria]   = useState('todos');
  const [nivelFiltro, setNivel]           = useState('todos');
  const [modalidadeFiltro, setModalidade] = useState('todos');
  const [ordenacao, setOrdenacao]         = useState('populares');
  const [currentPage, setCurrentPage]     = useState(1);
  const [mostrarFiltros, setMostrarFiltros] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => { setBusca(buscaInput); setCurrentPage(1); }, 400);
    return () => clearTimeout(t);
  }, [buscaInput]);

  useEffect(() => { setCurrentPage(1); }, [categoriaFiltro]);

  const fetchCursos = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams({ page: String(currentPage), limit: String(ITEMS_PER_PAGE) });
      if (busca)                       params.set('query', busca);
      if (categoriaFiltro !== 'todos') params.set('category', categoriaFiltro);
      const res = await fetch(`${API_BASE}/courses/search?${params}`);
      if (!res.ok) throw new Error(`Erro ${res.status}`);
      const data = await res.json();
      setCursos((data.courses ?? data.results ?? []).map(mapApiCurso));
      setTotalApi(data.total ?? 0);
    } catch (e: any) {
      setError(e.message ?? 'Erro ao carregar cursos');
    } finally { setLoading(false); }
  }, [busca, categoriaFiltro, currentPage]);

  useEffect(() => { fetchCursos(); }, [fetchCursos]);

  const cursosFiltrados = useMemo(() => cursos.filter(c => {
    const matchNivel      = nivelFiltro === 'todos'      || c.nivel === nivelFiltro;
    const matchModalidade = modalidadeFiltro === 'todos' || c.modalidade === modalidadeFiltro;
    return matchNivel && matchModalidade;
  }), [cursos, nivelFiltro, modalidadeFiltro]);

  const cursosOrdenados = useMemo(() => {
    const arr = [...cursosFiltrados];
    if (ordenacao === 'preco-baixo') return arr.sort((a, b) => a.preco - b.preco);
    if (ordenacao === 'preco-alto')  return arr.sort((a, b) => b.preco - a.preco);
    if (ordenacao === 'avaliacoes')  return arr.sort((a, b) => b.avaliacao - a.avaliacao);
    return arr.sort((a, b) => b.alunos - a.alunos);
  }, [cursosFiltrados, ordenacao]);

  const totalPages = Math.ceil(totalApi / ITEMS_PER_PAGE);

  const nivelLabel: Record<Curso['nivel'], string> = {
    basico: 'Básico', intermediario: 'Intermediário', avancado: 'Avançado',
  };
  const nivelCor: Record<Curso['nivel'], string> = {
    basico:        'bg-emerald-100 text-emerald-800',
    intermediario: 'bg-amber-100   text-amber-800',
    avancado:      'bg-red-100     text-red-800',
  };
  const modalidadeLabel: Record<Curso['modalidade'], string> = {
    presencial: 'Presencial', online: 'Online', hibrido: 'Híbrido',
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50/30">

      {/* ── HERO ──────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-950 via-amber-950 to-gray-950"/>
        <div className="absolute inset-0 opacity-15">
          <div className="absolute -top-24 -left-24 w-[520px] h-[520px] bg-amber-500 rounded-full blur-3xl"/>
          <div className="absolute -bottom-24 -right-24 w-[520px] h-[520px] bg-orange-500 rounded-full blur-3xl"/>
        </div>
        <div className="container mx-auto px-6 relative z-10 py-16 md:py-20 text-center">
          <div className="inline-flex items-center gap-3 px-6 py-3 bg-white/10 backdrop-blur-md rounded-full border border-white/20 mb-8">
            <GraduationCap className="w-5 h-5 text-amber-300"/>
            <span className="text-xs md:text-sm font-bold text-amber-100 uppercase tracking-wider">Cursos Oficiais</span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-4">
            Cursos Profissionais
          </h1>
          <p className="text-base md:text-lg text-white/85 max-w-2xl mx-auto mb-8">
            Desenvolva suas habilidades com cursos certificados por instrutores especializados da Federação.
          </p>
          {!loading && !error && totalApi > 0 && (
            <span className="inline-block px-4 py-2 rounded-full bg-white/10 border border-white/20 text-amber-200 text-sm font-semibold">
              {totalApi} curso{totalApi !== 1 ? 's' : ''} disponíve{totalApi !== 1 ? 'is' : 'l'}
            </span>
          )}
        </div>
        <div className="h-px w-full bg-gradient-to-r from-transparent via-white/10 to-transparent"/>
      </section>

      {/* ── BUSCA + FILTROS ────────────────────────────────────────────── */}
      <section className="py-10">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <div className="bg-white rounded-3xl border border-amber-100/70 shadow-[0_25px_60px_-35px_rgba(0,0,0,.35)] p-6 md:p-8">

              <div className="flex flex-col md:flex-row gap-4 mb-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"/>
                  <input
                    type="text" value={buscaInput}
                    onChange={e => setBuscaInput(e.target.value)}
                    placeholder="Buscar por nome, instrutor ou escola…"
                    className="w-full pl-12 pr-4 py-4 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all text-gray-800"
                  />
                </div>
                <button
                  onClick={() => setMostrarFiltros(v => !v)}
                  className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-2xl font-bold border border-gray-200 bg-gray-50 hover:bg-gray-100 transition-all"
                >
                  <Filter className="w-5 h-5"/> Filtros
                </button>
                <select
                  value={ordenacao} onChange={e => setOrdenacao(e.target.value)}
                  className="px-4 py-4 border border-gray-200 rounded-2xl bg-white text-gray-800 font-semibold focus:ring-2 focus:ring-amber-500"
                >
                  <option value="populares">Mais Populares</option>
                  <option value="preco-baixo">Preço (menor)</option>
                  <option value="preco-alto">Preço (maior)</option>
                  <option value="avaliacoes">Melhor Avaliados</option>
                </select>
              </div>

              {mostrarFiltros && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-gray-100">
                  <select value={categoriaFiltro} onChange={e => setCategoria(e.target.value)}
                    className="px-4 py-3 border border-gray-200 rounded-2xl bg-white text-gray-800 font-semibold">
                    <option value="todos">Todas as Categorias</option>
                    {['Cabelo','Maquiagem','Estética','Unhas','Barba','Spa','Cosmetologia','Massoterapia','Outro'].map(c =>
                      <option key={c} value={c}>{c}</option>)}
                  </select>
                  <select value={nivelFiltro} onChange={e => setNivel(e.target.value)}
                    className="px-4 py-3 border border-gray-200 rounded-2xl bg-white text-gray-800 font-semibold">
                    <option value="todos">Todos os Níveis</option>
                    <option value="basico">Básico</option>
                    <option value="intermediario">Intermediário</option>
                    <option value="avancado">Avançado</option>
                  </select>
                  <select value={modalidadeFiltro} onChange={e => setModalidade(e.target.value)}
                    className="px-4 py-3 border border-gray-200 rounded-2xl bg-white text-gray-800 font-semibold">
                    <option value="todos">Todas as Modalidades</option>
                    <option value="presencial">Presencial</option>
                    <option value="online">Online</option>
                    <option value="hibrido">Híbrido</option>
                  </select>
                </div>
              )}

              {!loading && (
                <p className="mt-4 text-sm text-gray-500">
                  <span className="font-black text-amber-700">{cursosOrdenados.length}</span> resultado{cursosOrdenados.length !== 1 ? 's' : ''}
                </p>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── GRID ──────────────────────────────────────────────────────── */}
      <section className="pb-20">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">

            {/* Loading */}
            {loading && (
              <div className="flex flex-col items-center gap-4 py-24">
                <Loader2 className="w-10 h-10 text-amber-600 animate-spin"/>
                <p className="text-gray-500 font-semibold">Carregando cursos…</p>
              </div>
            )}

            {/* Erro */}
            {!loading && error && (
              <div className="flex flex-col items-center gap-4 py-24">
                <AlertTriangle className="w-10 h-10 text-red-400"/>
                <p className="text-red-600 font-bold">{error}</p>
                <button onClick={fetchCursos}
                  className="px-6 py-3 rounded-2xl font-bold bg-gray-900 text-white hover:shadow-lg transition-all">
                  Tentar novamente
                </button>
              </div>
            )}

            {/* Lista */}
            {!loading && !error && (
              <>
                {cursosOrdenados.length > 0 ? (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {cursosOrdenados.map(curso => (
                        <div key={curso.id}
                          className="bg-white rounded-3xl border border-amber-100/70 overflow-hidden shadow-[0_20px_55px_-40px_rgba(0,0,0,.45)] hover:shadow-[0_28px_70px_-40px_rgba(0,0,0,.55)] transition-all group flex flex-col">

                          {/* Thumb */}
                          <div className="w-full h-44 relative overflow-hidden bg-gradient-to-br from-amber-100 to-orange-100">
                            {curso.coverImageUrl ? (
                              <img src={curso.coverImageUrl} alt={curso.titulo}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"/>
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <BookOpen className="w-14 h-14 text-amber-400/60 group-hover:scale-110 transition-transform duration-300"/>
                              </div>
                            )}
                            <span className="absolute top-3 right-3 px-2.5 py-1 rounded-full text-xs font-bold bg-white/80 backdrop-blur-sm text-amber-800 border border-amber-200">
                              {modalidadeLabel[curso.modalidade]}
                            </span>
                          </div>

                          {/* Conteúdo */}
                          <div className="p-6 flex flex-col flex-1">
                            {/* Badges */}
                            <div className="flex flex-wrap gap-2 mb-3">
                              <span className={`text-xs px-2.5 py-1 rounded-full font-bold ${nivelCor[curso.nivel]}`}>
                                {nivelLabel[curso.nivel]}
                              </span>
                              {curso.categoria && (
                                <span className="text-xs px-2.5 py-1 rounded-full font-bold bg-gray-100 text-gray-700">
                                  {curso.categoria}
                                </span>
                              )}
                            </div>

                            {/* Título */}
                            <h3 className="text-base font-black text-gray-900 mb-2 line-clamp-2 flex-shrink-0">
                              {curso.titulo}
                            </h3>

                            {/* Descrição */}
                            {curso.descricao && (
                              <p className="text-sm text-gray-500 mb-4 line-clamp-2">{curso.descricao}</p>
                            )}

                            {/* Métricas */}
                            <div className="space-y-1.5 mb-4 text-sm text-gray-500 flex-1">
                              {curso.instrutor && (
                                <div className="flex items-center gap-2 text-xs">
                                  <span>👤</span>
                                  <span className="truncate">{curso.instrutor}</span>
                                </div>
                              )}
                              {curso.afiliado && (
                                <div className="flex items-center gap-2 text-xs">
                                  <Building2 className="w-3.5 h-3.5 text-amber-500 flex-shrink-0"/>
                                  <span className="truncate">{curso.afiliado}</span>
                                </div>
                              )}
                              <div className="flex items-center gap-4 text-xs">
                                {curso.duracao !== '—' && (
                                  <span className="flex items-center gap-1">
                                    <Clock className="w-3.5 h-3.5"/> {curso.duracao}
                                  </span>
                                )}
                                {curso.alunos > 0 && (
                                  <span className="flex items-center gap-1">
                                    <Users className="w-3.5 h-3.5"/> {curso.alunos}
                                  </span>
                                )}
                                {curso.avaliacao > 0 && (
                                  <span className="flex items-center gap-1">
                                    <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500"/>
                                    {curso.avaliacao.toFixed(1)}
                                  </span>
                                )}
                              </div>
                            </div>

                            {/* Footer */}
                            <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                              <span className="text-xl font-black text-amber-700">
                                {curso.preco > 0 ? `R$ ${curso.preco.toFixed(2)}` : 'Gratuito'}
                              </span>
                              <Link
                                to={`/cursos/${curso.id}`}
                                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-2xl text-sm font-bold text-gray-900 hover:shadow-lg transition-all group/btn"
                                style={{ background: 'linear-gradient(135deg,#f4d03f,#d4af37)' }}
                              >
                                Ver mais
                                <ArrowRight className="w-3.5 h-3.5 group-hover/btn:translate-x-0.5 transition-transform"/>
                              </Link>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Paginação */}
                    {totalPages > 1 && (
                      <div className="flex justify-center items-center gap-3 mt-12">
                        <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                          disabled={currentPage === 1}
                          className="px-5 py-2.5 rounded-2xl border border-gray-200 font-bold text-gray-700 hover:bg-gray-50 disabled:opacity-40 transition-all text-sm">
                          ← Anterior
                        </button>
                        <span className="px-5 py-2.5 rounded-2xl bg-white border border-amber-200 text-sm font-black text-amber-700">
                          {currentPage} / {totalPages}
                        </span>
                        <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                          disabled={currentPage === totalPages}
                          className="px-5 py-2.5 rounded-2xl border border-gray-200 font-bold text-gray-700 hover:bg-gray-50 disabled:opacity-40 transition-all text-sm">
                          Próximo →
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  /* Vazio */
                  <div className="text-center py-24 bg-white rounded-3xl border border-amber-100/70">
                    <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                      <BookOpen className="w-10 h-10 text-amber-300"/>
                    </div>
                    <h3 className="text-xl font-black text-gray-900 mb-2">Nenhum curso encontrado</h3>
                    <p className="text-gray-500 text-sm mb-6">
                      {busca || categoriaFiltro !== 'todos' || nivelFiltro !== 'todos' || modalidadeFiltro !== 'todos'
                        ? 'Tente ajustar os filtros.'
                        : 'Novos cursos serão adicionados em breve.'}
                    </p>
                    {(busca || categoriaFiltro !== 'todos') && (
                      <button onClick={() => { setBuscaInput(''); setCategoria('todos'); setNivel('todos'); setModalidade('todos'); }}
                        className="px-6 py-3 rounded-2xl font-bold bg-gray-900 text-white hover:shadow-lg transition-all text-sm">
                        Limpar filtros
                      </button>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Cursos;